import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { object, string } from 'yup';
import { useQueryParams } from '@/hooks';
import { AccepetedIntro, IntroTextResult } from './introResults';
import { getMessage } from './constant';
import {
  useAddEmailPublicIntroMutation,
  useDeclinePublicIntroMutation,
  useGetPublicIntroByGuidQuery,
} from '@/store/query/public-intros';
import { useState } from 'react';
import Logo from '@/icons/Logo';
import { PublicDeny } from '@/assets/icons/intros';
import { HiCheckCircle } from 'react-icons/hi';
import { useShowToast } from '@/hooks/useShowToast';

const validationSchema = object().shape({
  email: string()
    .nullable()
    .required('Email is required')
    .email('Please enter a valid email address'),
});

export const PublicIntroEmail = () => {
  const params = useQueryParams();
  const introId = params.get('introId');
  const toast = useShowToast();
  const [addEmailPublicIntro, { isLoading: isaddEmailLoading }] =
    useAddEmailPublicIntroMutation();
  const [declinePublicIntro, { isLoading: isDeclineLoading }] =
    useDeclinePublicIntroMutation();
  const [introStatus, setStatus] = useState(null);
  const {
    isFetching,
    data: intro,
    error,
  } = useGetPublicIntroByGuidQuery(
    {
      introId,
    },
    { skip: !introId?.length }
  );

  // TODO Handle submit email
  const onSubmit = async (values) => {
    const res = await addEmailPublicIntro({
      introId: introId,
      email: values.email,
    });
    if (res?.error?.length) {
      toast({
        title: 'Adding email failed',
        description: res?.error[0],
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    setStatus('intro_email_added');
  };

  const onDecline = async () => {
    const res = await declinePublicIntro({
      introId: introId,
    });
    if (res?.error?.length) {
      toast({
        title: 'Decline intro failed',
        description: res?.error[0],
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    setStatus('intro_declined');
  };

  if (isFetching) {
    return (
      <Flex h="100vh" justifyContent="center" alignItems="center">
        <Spinner color="#0043CE" />
      </Flex>
    );
  }

  if (error?.length) {
    return <IntroTextResult text={getMessage(error[0])} />;
  }

  if (!introId?.length) {
    return <IntroTextResult text={getMessage('required_parmas_not_found')} />;
  }

  if (intro?.status === 'make_intro_done') {
    return (
      <IntroTextResult
        text={getMessage('make_intro_done')}
        isIconPresent={true}
        icon={
          <Icon
            as={HiCheckCircle}
            width="55px"
            height="55px"
            mb="20px"
            color="#24A148"
          />
        }
      />
    );
  }

  if (intro?.status === 'make_intro_fail') {
    return (
      <IntroTextResult
        text={getMessage('make_intro_fail')}
        isIconPresent={true}
        icon={
          <Icon
            as={HiCheckCircle}
            width="55px"
            height="55px"
            mb="20px"
            color="#24A148"
          />
        }
      />
    );
  }

  if (intro?.status === 'make_intro_request_permission_rejected') {
    return (
      <IntroTextResult
        text={getMessage('make_intro_request_permission_rejected')}
      />
    );
  }

  if (intro?.status === 'make_intro_consent') {
    return (
      <IntroTextResult
        text={getMessage('make_intro_consent')}
        isIconPresent={true}
        icon={
          <Icon
            as={HiCheckCircle}
            width="55px"
            height="55px"
            mb="20px"
            color="#24A148"
          />
        }
      />
    );
  }

  if (introStatus === 'intro_declined') {
    return (
      <IntroTextResult
        text={getMessage('intro_declined')}
        isIconPresent={true}
        icon={<Icon as={PublicDeny} width="161px" height="135px" mb="20px" />}
      />
    );
  }

  if (introStatus === 'intro_email_added') {
    return <AccepetedIntro intro={intro} />;
  }

  return (
    <Flex h="100vh">
      <Flex w="50%" justifyContent="center">
        <Flex
          justifyContent="center"
          maxW="410px"
          ml="auto"
          flexDir="column"
          mr="36px"
          pos="relative"
        >
          <Text fontSize="26px" fontWeight="semiBold" mb="12px">
            Hi {intro?.contact?.name}!
          </Text>
          <Text mb="36px">
            We’re glad you decided to make the intro.Make sure to include an
            email so that the recruiter can reach you.
          </Text>
          <Text fontWeight="semiBold" mb="20px">
            Introducing to
          </Text>
          <Flex gap="16px" alignItems="center">
            <Avatar
              w="52px"
              h="52px"
              borderRadius="12px"
              src={intro?.requester?.avatar}
              name={intro?.requester?.name}
            />
            <Text fontSize="20px" fontWeight="semiBold">
              {intro?.requester?.name}
            </Text>
          </Flex>
          <Box pos="absolute" bottom="30px">
            <Icon as={Logo} width="32px" height="32px" />
            <Text fontWeight="semiBold" mt="12px">
              Powered by kula
            </Text>
          </Box>
        </Flex>
      </Flex>
      <Flex w="50%" alignItems="center" className="public-email-bg">
        <Flex ml="60px">
          <Formik
            validateOnBlur={false}
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            <Form noValidate>
              <Field name="email">
                {({ field, form }) => (
                  <FormControl
                    mb="16px"
                    isRequired
                    isInvalid={form.errors.email && form.touched.email}
                  >
                    <FormLabel
                      fontWeight="semiBold"
                      fontSize="14px"
                      htmlFor="email"
                    >
                      Email
                    </FormLabel>
                    <Input
                      {...field}
                      id="email"
                      placeholder="Enter your email"
                      data-test-id="public-input-email"
                    />
                    <FormErrorMessage>
                      <ErrorMessage name="email" />
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Flex gap="16px">
                <Button
                  type="submit"
                  isLoading={isaddEmailLoading}
                  data-test-id="public-addemail"
                >
                  Accept intro
                </Button>
                <Button
                  variant="secondary"
                  onClick={onDecline}
                  isLoading={isDeclineLoading}
                  data-test-id="public-decline"
                >
                  Decline
                </Button>
              </Flex>
            </Form>
          </Formik>
        </Flex>
      </Flex>
    </Flex>
  );
};
